import { Loading, SplitPanel } from '@components/atomic/atoms';
import CheckList from '@components/checklist/checkList';
import { UserContext } from '@components/contexts/UserProvider';
import UploadDocument from '@components/form/upload/uploadDocuments';
import { IcoClose } from '@icons';
import { getModalData } from '@lib/api';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useContext, useState } from 'react';
import { Button, Modal /*, Tab, Tabs*/ } from 'react-bootstrap';
import SummaryInvoiceModalInfo from './SummaryInvoiceModalInfo';
import { ResumeChecklistProjectType } from './ResumeChecklistCellRenderer';
import SummaryDownloadList from './SummaryDownloadList';
import { useHasPermission } from '@lib/hooks/useHasPermission';

interface SummaryInvoiceModalProps {
	show: boolean;
	onHide: () => void;
	apiUrl: string;
	project: ResumeChecklistProjectType;
	sections?: Record<string, string | boolean>;
	// projectName: string;
	// streamConfigurationType: string;
}

export type InvoiceDataType = {
	supplierName?: string;
	supplierCode?: string;
	supplierVat?: string;
	date?: string;
	number?: string;
	rpCode?: string;
	contract?: {
		po: boolean;
		number?: string;
		frameworkContract?: {
			number?: string;
		};
	};
	project?: {
		cupType?: string;
		cup?: string;
		startDate?: string;
	};
	tenderCig?: {
		tender?: {
			tenderNumber?: string;
		};
		cig?: string;
	};
	taxableAmount?: number;
	vatAmount?: number;
	totalAmount?: number;
	stream?: {
		streamId?: string;
		name?: string;
		project: {
			cup?: string;
		};
	};
	regisTaxonomy?: string;
	internalTaxonomy?: string;
	rdiTypeCode?: string;
	paymentCup?: string;
	paymentCig?: string;
	payment?: {
		cro?: string;
	};
	id: string;
	payments?: {
		pivot: {
			id: string;
		};
	}[];
	contractId?: string;
	paymentId?: string;
	reported?: boolean;
	reporting?: boolean;
	checkliststatus: {
		color: string;
	};
	sections: Record<string, string>;
};

const SummaryInvoiceModal: React.FC<SummaryInvoiceModalProps> = ({ show, onHide: onHideFromParent, apiUrl, project, sections }) => {
	const { userAccess } = useContext(UserContext);

	const { data: modalData, isPending: isPendingModalData } = useQuery<{
		data: InvoiceDataType;
	}>({
		queryKey: ['modalData', apiUrl],
		queryFn: () => getModalData(apiUrl),
		enabled: show,
	});
	const invoiceData = modalData?.data;

	// const [activeKey, setActiveKey] = useState('checklist-invoices');

	const queryClient = useQueryClient();
	const invalidateInvoicesQuery = () => {
		queryClient.invalidateQueries({ queryKey: ['resumeInvoices'] });
	};

	const [isEdited, setIsEdited] = useState(false);
	const onHide = () => {
		if (isEdited) {
			invalidateInvoicesQuery();
		}
		setIsEdited(false);
		onHideFromParent();
	};

	const isDisabled = invoiceData?.reported || invoiceData?.reporting || (!userAccess.isPartner && !userAccess.isGuestEnteErogante);

	const shouldShowChecklist = useHasPermission('view-checklists');

	return (
		<Modal fullscreen show={show} onHide={onHide}>
			<Modal.Header
				style={{
					padding: '2rem',
					justifyContent: 'space-between',
				}}
			>
				<Modal.Title>
					<h2 style={{ fontSize: '28px', marginBottom: '0.5rem' }}>Controllo della fattura {invoiceData?.id}</h2>
					<h3
						style={{
							fontSize: '18px',
							fontWeight: 700,
						}}
					>
						PROGETTO: {project.name}
					</h3>
				</Modal.Title>
				<Button
					className="btn custom-ghost-button"
					onClick={onHide}
					style={{
						// color: '#06797F',
						display: 'flex',
						alignItems: 'center',
						gap: '0.75rem',
					}}
				>
					Chiudi
					<IcoClose
					// style={{
					// 	fill: '#06797F',
					// }}
					/>
				</Button>
			</Modal.Header>
			<Modal.Body
				style={{
					padding: '2rem',
					overflow: 'hidden',
				}}
			>
				{isPendingModalData ? (
					<Loading />
				) : show ? (
					<SplitPanel
						panels={[
							{
								key: 'info',
								title: 'Informazioni',
								content: (
									<SummaryInvoiceModalInfo
										invoiceData={invoiceData}
										project={project}
										// sections={sections}
									/>
								),
								...(shouldShowChecklist ? { defaultIsVisible: false } : {}),
							},
							{
								key: 'documents',
								title: 'Documenti',
								content: (
									<div>
										<UploadDocument entity="invoices" entityId={invoiceData.id} title="Documenti correlati" />

										{invoiceData.paymentId && (
											<UploadDocument
												entity="payments"
												entityId={invoiceData.paymentId}
												disableDelete={true}
												disableEdit={!userAccess.isPartner}
												title="Documentazione Giustificativa di pagamento"
											/>
										)}

										{invoiceData.contractId && (
											<UploadDocument
												entity="contracts"
												entityId={invoiceData.contractId}
												disableEdit={!userAccess.isPartner}
												title="Documenti contratto"
											/>
										)}
									</div>
								),
							},
							...(shouldShowChecklist
								? [
										{
											key: 'checklist',
											title: 'Checklist',
											content: (
												<div className="custom-checklist-container">
													<div>
														<h4>Checklist per il controllo</h4>
														<SummaryDownloadList
															entity={{
																entityType: 'invoices',
																entityId: invoiceData.id,
																label: 'fatture',
															}}
															extraEntity={{
																entityType: 'invoicePayments',
																entityId: invoiceData?.payments?.[0]?.pivot?.id,
																label: 'pagamenti',
															}}
															//  entityType="invoices" entityId={invoiceData.id} label="fatture"
														/>
													</div>
													{/* <Tabs
											fill
											variant="pills"
											onSelect={(key) => {
												setActiveKey(key);
											}}
										>
											<Tab eventKey="checklist-invoices" title={'Checklist Fatture'} className="px-2 pt-3"> */}
													<h4>Fatture</h4>
													{invoiceData.id && (
														<CheckList
															hasStandardChecklist={false}
															entityType="invoices"
															entityId={invoiceData.id}
															disabled={isDisabled}
															hasInternalDownload={false}
															hasEnabledAi={project.aiEnabled}
														/>
													)}
													{/* </Tab>
											<Tab eventKey="checklist-invoicePayments" title={'Checklist Pagamenti'} className="px-2 pt-3"> */}
													<h4>Pagamenti</h4>
													{invoiceData.payments?.length > 0 ? (
														<CheckList
															hasStandardChecklist={false}
															entityType="invoicePayments"
															entityId={invoiceData?.payments?.[0]?.pivot?.id}
															disabled={isDisabled}
															hasInternalDownload={false}
															hasEnabledAi={project.aiEnabled}
														/>
													) : (
														<div className="text-center mt-4">
															<span>Nessun pagamento associato a questo elemento</span>
														</div>
													)}
													{/* </Tab>
										</Tabs> */}
												</div>
											),
										},
									]
								: []),
						]}
					/>
				) : null}
			</Modal.Body>
			<Modal.Footer
				style={{
					justifyContent: 'space-between',
				}}
			>
				<Button onClick={onHide} className="btn custom-outline-button custom-medium-button">
					Annulla
				</Button>
				{shouldShowChecklist ? (
					<div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
						<div>Salva risposte:</div>

						<Button
							onClick={() => {
								setIsEdited(true);
							}}
							disabled={isDisabled}
							type="submit"
							form="checklist-invoices"
							className="btn custom-primary-button custom-medium-button"
						>
							checklist fatture
						</Button>
						<Button
							onClick={() => {
								setIsEdited(true);
							}}
							disabled={isDisabled}
							type="submit"
							form="checklist-invoicePayments"
							className="btn custom-primary-button custom-medium-button"
						>
							checklist pagamenti
						</Button>
					</div>
				) : null}
			</Modal.Footer>
		</Modal>
	);
};

export default SummaryInvoiceModal;
